import { combineReducers } from 'redux';

import { setEnabled } from 'Config';
import { createKeyNotInTransform } from 'Lib/persist/transforms';
import type { Reducer } from 'Lib/reduxType';

import { getCommonResetAction, LAYOUT_CHANGED } from 'Store/commonActions';

import { createMetaReducer } from '../meta/utils';
import {
  SET_APP_START_INFO,
  SET_CHAT_SUGGESTION_CONSENT_SHOWN,
  SET_CHAT_SUGGESTIONS_CONSENTS_SUCCESS,
  SET_DISTANCE_UNIT,
  SET_IS_HUBBLE_DEBUGGER_ENABLED,
  SET_THEME_DEVICE_PREFERENCE,
  SET_THEME_PREFERENCE,
  TOGGLE_CONSUMABLES,
  TOGGLE_GOLD,
  TOGGLE_PLATINUM,
  TOGGLE_RECS_TOOLBAR,
  TOGGLE_TINDER_PLUS,
} from './constants';
import initialState from './initialState';
import type { AppSettingsState } from './types';

const ageVerificationStatusReducer = createMetaReducer(initialState, 'ageVerificationStatus');
const ageVerificationReasonReducer = createMetaReducer(initialState, 'ageVerificationReason');
const ageVerificationShowSessionOnboardingModalReducer = createMetaReducer(
  initialState,
  'ageVerificationShowSessionOnboardingModal'
);
const ageVerificationIsSessionOnboardingSkippableReducer = createMetaReducer(
  initialState,
  'ageVerificationIsSessionOnboardingSkippable'
);
const biometricConsentsReducer = createMetaReducer(initialState, 'biometricConsents', true);
const biometricLivenessReducer = createMetaReducer(initialState, 'biometricLivenessState');
const canEditEmailReducer = createMetaReducer(initialState, 'canEditEmail');
const companyMaxLengthReducer = createMetaReducer(initialState, 'companyMaxLength');
const consentReducer = createMetaReducer(initialState, 'consent', true);
const emailVerificationDismissibleReducer = createMetaReducer(
  initialState,
  'emailVerificationDismissible',
  true
);
const emailVerificationRequiredReducer = createMetaReducer(
  initialState,
  'emailVerificationRequired',
  true
);

const chatSuggestionConsentShownReducer: Reducer<AppSettingsState['chatSuggestionConsentShown']> = (
  state = initialState.chatSuggestionConsentShown,
  action
) => {
  switch (action.type) {
    case SET_CHAT_SUGGESTION_CONSENT_SHOWN: {
      const { payload: shown } = action;
      return shown;
    }
    default:
      return state;
  }
};

const chatSuggestionConsentReducer: Reducer<AppSettingsState['chatSuggestionConsents']> = (
  state = initialState.chatSuggestionConsents,
  action
) => {
  switch (action.type) {
    case SET_CHAT_SUGGESTIONS_CONSENTS_SUCCESS: {
      const { payload: result } = action;
      return result;
    }
    default:
      return state;
  }
};

const globalPassportTitleReducer = createMetaReducer(initialState, 'globalPassportTitle');
const globalPassportDescriptionReducer = createMetaReducer(
  initialState,
  'globalPassportDescription'
);
const isBiometricConsentNeededReducer = createMetaReducer(
  initialState,
  'isBiometricConsentNeeded',
  true
);
const isMessageConsentNeededReducer = createMetaReducer(
  initialState,
  'isMessageConsentNeeded',
  true
);
const isTermsAcceptanceNeededReducer = createMetaReducer(
  initialState,
  'isTermsAcceptanceNeeded',
  true
);
const jobTitleMaxLengthReducer = createMetaReducer(initialState, 'jobTitleMaxLength');
const orientationEnabledReducer = createMetaReducer(initialState, 'orientationEnabled');
const orientationsReducer = createMetaReducer(initialState, 'orientations');
const merchandisingFeaturesReducer = createMetaReducer(initialState, 'merchandisingFeatures');
const messageConsentsReducer = createMetaReducer(initialState, 'messageConsents', true);
const paywallFeaturesReducer = createMetaReducer(initialState, 'paywallFeatures');
const platinumPaywallFeaturesReducer = createMetaReducer(initialState, 'platinumPaywallFeatures');
const schoolMaxLengthReducer = createMetaReducer(initialState, 'schoolMaxLength');
const showTermsReducer = createMetaReducer(initialState, 'showTerms', true);
const superBoostMembersTextReducer = createMetaReducer(initialState, 'superBoostMembersText');
const superlikeAlcModeReducer = createMetaReducer(initialState, 'superlikeAlcMode');
const termsVersionReducer = createMetaReducer(initialState, 'termsVersion', true);
const roomStatusPollingIntervalReducer = createMetaReducer(
  initialState,
  'roomStatusPollingInterval'
);
const typingPulseIntervalReducer = createMetaReducer(initialState, 'typingPulseInterval');
const typingTimeoutReducer = createMetaReducer(initialState, 'typingTimeout');

const recsToolbarIsOpenedReducer: Reducer<AppSettingsState['recsToolbarIsOpened']> = (
  state = initialState.recsToolbarIsOpened,
  action
) => {
  switch (action.type) {
    case TOGGLE_RECS_TOOLBAR: {
      const { payload: shouldOpenToolbar } = action;
      return shouldOpenToolbar;
    }
    default:
      return state;
  }
};

const layoutReducer: Reducer<AppSettingsState['layout']> = (
  state = initialState.layout,
  action
) => {
  switch (action.type) {
    case LAYOUT_CHANGED:
      return action.payload?.layout ?? state;
    default:
      return state;
  }
};

const themeDevicePreferenceReducer: Reducer<AppSettingsState['themeDevicePreference']> = (
  state = initialState.themeDevicePreference,
  action
) => {
  switch (action.type) {
    case SET_THEME_DEVICE_PREFERENCE: {
      const { payload: themeDevicePreference } = action;
      return themeDevicePreference;
    }
    default:
      return state;
  }
};

const themePreferenceReducer: Reducer<AppSettingsState['themePreference']> = (
  state = initialState.themePreference,
  action
) => {
  switch (action.type) {
    case SET_THEME_PREFERENCE: {
      const { payload: themePreference } = action;
      return themePreference;
    }
    default:
      return state;
  }
};

const tinderPlusEnabledReducer: Reducer<AppSettingsState['isTinderPlusEnabled']> = (
  state = initialState.isTinderPlusEnabled,
  action
) => {
  switch (action.type) {
    case TOGGLE_TINDER_PLUS: {
      const { payload: shouldEnabled } = action;
      setEnabled('tinderPlus', shouldEnabled);
      return shouldEnabled;
    }
    default:
      return state;
  }
};

const goldEnabledReducer: Reducer<AppSettingsState['isGoldEnabled']> = (
  state = initialState.isGoldEnabled,
  action
) => {
  switch (action.type) {
    case TOGGLE_GOLD: {
      const { payload: shouldEnabled } = action;
      setEnabled('gold', shouldEnabled);
      return shouldEnabled;
    }
    default:
      return state;
  }
};

const platinumEnabledReducer: Reducer<AppSettingsState['isPlatinumEnabled']> = (
  state = initialState.isPlatinumEnabled,
  action
) => {
  switch (action.type) {
    case TOGGLE_PLATINUM: {
      const { payload: shouldEnabled } = action;
      setEnabled('platinum', shouldEnabled);
      return shouldEnabled;
    }
    default:
      return state;
  }
};

const consumablesEnabledReducer: Reducer<AppSettingsState['isConsumablesEnabled']> = (
  state = initialState.isConsumablesEnabled,
  action
) => {
  switch (action.type) {
    case TOGGLE_CONSUMABLES: {
      const { payload: shouldEnabled } = action;
      setEnabled('consumables', shouldEnabled);
      return shouldEnabled;
    }
    default:
      return state;
  }
};

const appStartInfoReducer = (
  state: AppSettingsState['appStartInfo'] = initialState.appStartInfo,
  action
) => {
  switch (action.type) {
    case SET_APP_START_INFO:
      return action.payload;
    default:
      return state;
  }
};

const distanceUnitReducer: Reducer<AppSettingsState['distanceUnit']> = (
  state = initialState.distanceUnit,
  action
) => {
  switch (action.type) {
    case SET_DISTANCE_UNIT: {
      return action.payload;
    }
    case getCommonResetAction(action.type): {
      return initialState.distanceUnit;
    }
    default:
      return state;
  }
};

const isHubbleDebuggerEnabledReducer = (
  state: AppSettingsState['isHubbleDebuggerEnabled'] = initialState.isHubbleDebuggerEnabled,
  action
) => {
  switch (action.type) {
    case SET_IS_HUBBLE_DEBUGGER_ENABLED:
      return action.payload;
    default:
      return state;
  }
};

export const reducer = combineReducers({
  appStartInfo: appStartInfoReducer,
  ageVerificationStatus: ageVerificationStatusReducer,
  ageVerificationReason: ageVerificationReasonReducer,
  ageVerificationShowSessionOnboardingModal: ageVerificationShowSessionOnboardingModalReducer,
  ageVerificationIsSessionOnboardingSkippable: ageVerificationIsSessionOnboardingSkippableReducer,
  biometricConsents: biometricConsentsReducer,
  biometricLivenessState: biometricLivenessReducer,
  distanceUnit: distanceUnitReducer,
  canEditEmail: canEditEmailReducer,
  chatSuggestionConsents: chatSuggestionConsentReducer,
  companyMaxLength: companyMaxLengthReducer,
  consent: consentReducer,
  emailVerificationDismissible: emailVerificationDismissibleReducer,
  emailVerificationRequired: emailVerificationRequiredReducer,
  globalPassportTitle: globalPassportTitleReducer,
  globalPassportDescription: globalPassportDescriptionReducer,
  chatSuggestionConsentShown: chatSuggestionConsentShownReducer,
  isBiometricConsentNeeded: isBiometricConsentNeededReducer,
  isConsumablesEnabled: consumablesEnabledReducer,
  isGoldEnabled: goldEnabledReducer,
  isHubbleDebuggerEnabled: isHubbleDebuggerEnabledReducer,
  isPlatinumEnabled: platinumEnabledReducer,
  isMessageConsentNeeded: isMessageConsentNeededReducer,
  isTermsAcceptanceNeeded: isTermsAcceptanceNeededReducer,
  isTinderPlusEnabled: tinderPlusEnabledReducer,
  jobTitleMaxLength: jobTitleMaxLengthReducer,
  layout: layoutReducer,
  merchandisingFeatures: merchandisingFeaturesReducer,
  messageConsents: messageConsentsReducer,
  orientationEnabled: orientationEnabledReducer,
  orientations: orientationsReducer,
  paywallFeatures: paywallFeaturesReducer,
  platinumPaywallFeatures: platinumPaywallFeaturesReducer,
  recsToolbarIsOpened: recsToolbarIsOpenedReducer,
  roomStatusPollingInterval: roomStatusPollingIntervalReducer,
  schoolMaxLength: schoolMaxLengthReducer,
  showTerms: showTermsReducer,
  superBoostMembersText: superBoostMembersTextReducer,
  superlikeAlcMode: superlikeAlcModeReducer,
  termsVersion: termsVersionReducer,
  themeDevicePreference: themeDevicePreferenceReducer,
  themePreference: themePreferenceReducer,
  typingPulseInterval: typingPulseIntervalReducer,
  typingTimeout: typingTimeoutReducer,
});

export const persistConfig = {
  transforms: [
    createKeyNotInTransform([
      'appStartInfo',
      'biometricLivenessState',
      'isGoldEnabled',
      'isConsumablesEnabled',
      'isTinderPlusEnabled',
      'isMessageConsentNeeded',
      'layout',
    ]),
  ],
  version: 12,
};
